import { useCallback, useMemo, useRef } from 'react';
import { Task, FullProject, LogEntry, ProjectMetadata } from '../../types';
import { generateNextRecurringTask } from '../../utils/recurrenceUtils';
import confetti from 'canvas-confetti';
import { areProjectIdsMatching } from '../../utils/idNormalizer';
import {
  executeTaskMutationBatch,
  commitTaskListUpdate,
  TaskMutationBatchItem,
} from './crud/taskMutationBatchEngine';
import { useProjectMetadataActions } from './crud/useProjectMetadataActions';
import { useTaskDeleteActions } from './crud/useTaskDeleteActions';
import { useTaskDuplicateAndMove } from './crud/useTaskDuplicateAndMove';
import { useTaskAddAction } from './crud/useTaskAddAction';
import { createProjectSyncTracker, ProjectSyncTracker } from './sync/projectSyncStateTracker';

export interface UseTaskCRUDProps {
  allProjects: FullProject[];
  syncTracker?: ProjectSyncTracker;
  allProjectsRef: React.MutableRefObject<FullProject[]>;
  rawProjectsRef?: React.MutableRefObject<FullProject[]>;
  rawTasksRef?: React.MutableRefObject<Task[]>;
  ownedTasksRef?: React.MutableRefObject<Task[]>;
  migratedProjectIdsRef: React.MutableRefObject<Set<string>>;
  setAllProjects: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void;
  currentProjectId: string;
  currentProjectIdRef: React.MutableRefObject<string>;
  tasks: Task[];
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  setProjectMetadata: React.Dispatch<React.SetStateAction<ProjectMetadata>>;
  saveSnapshot: () => void;
  addLogEntry: (action: LogEntry['action'], description: string, details?: string) => void;
  markProjectDirty: (id: string) => void;
  onMarkTaskDirty: (taskId: string | number, projectId?: string) => void;
  dirtyTaskIdsRef?: React.MutableRefObject<Set<string>>;
  recentlyDeletedTaskIdsRef?: React.MutableRefObject<Set<string>>;
  syncProjectToFirestore: (project: FullProject, isBackground?: boolean) => Promise<boolean | void | number | null>;
  user: { uid?: string; email?: string | null; displayName?: string | null } | null;
  tenantId: string;
  COMM_PROJECT_ID: string;
  INBOX_PROJECT_ID: string;
  normalizeProjectId: (id?: string) => string | undefined;
  getProjectTasksWithCommVorgaenge?: (proj: FullProject, projectsList: FullProject[]) => Task[];
}

export function useTaskCRUD({
  syncTracker: propSyncTracker,
  allProjectsRef,
  rawProjectsRef,
  rawTasksRef,
  ownedTasksRef,
  setAllProjects,
  currentProjectId,
  tasks,
  setTasks,
  setProjectMetadata,
  saveSnapshot,
  addLogEntry,
  markProjectDirty,
  onMarkTaskDirty,
  dirtyTaskIdsRef,
  recentlyDeletedTaskIdsRef,
  user,
  tenantId,
  COMM_PROJECT_ID,
  INBOX_PROJECT_ID,
  normalizeProjectId,
  getProjectTasksWithCommVorgaenge,
}: UseTaskCRUDProps) {
  const tracker = useMemo(
    () =>
      propSyncTracker ??
      createProjectSyncTracker({
        allProjectsRef,
        rawProjectsRef,
        rawTasksRef,
        ownedTasksRef,
        dirtyTaskIdsRef,
        recentlyDeletedTaskIdsRef,
      }),
    [propSyncTracker, allProjectsRef, rawProjectsRef, rawTasksRef, ownedTasksRef, dirtyTaskIdsRef, recentlyDeletedTaskIdsRef]
  );

  const updateCurrentProjectTasks = useCallback(
    (currentProj: FullProject, updatedProjList: FullProject[]) => {
      const merged = getProjectTasksWithCommVorgaenge
        ? getProjectTasksWithCommVorgaenge(currentProj, updatedProjList)
        : (currentProj.tasks || []);
      setTasks(merged);
    },
    [getProjectTasksWithCommVorgaenge, setTasks]
  );

  type AddTaskFn = (type?: Task['type'], parentId?: string | number, projectId?: string, name?: string, initialData?: Partial<Task>, options?: { bypassDragGuard?: boolean; isInlineNew?: boolean; initialSortOrder?: number }) => Task | undefined;
  const addTaskRef = useRef<AddTaskFn | null>(null);

  const commitTasks = useCallback(
    (newTasks: Task[], logAction?: LogEntry['action'], logDescription?: string) => {
      saveSnapshot();
      commitTaskListUpdate({
        newTasks,
        currentProjectId,
        tracker,
        setAllProjects,
        setTasks,
        updateCurrentProjectTasks,
        markProjectDirty,
        onMarkTaskDirty,
      });

      if (logAction && logDescription) {
        addLogEntry(logAction, logDescription);
      }
    },
    [
      currentProjectId,
      addLogEntry,
      markProjectDirty,
      onMarkTaskDirty,
      saveSnapshot,
      setAllProjects,
      setTasks,
      tracker,
      updateCurrentProjectTasks,
    ]
  );

  /**
   * Unified mutation executor shared by both `updateTask` (single item) and `updateTasksMultiple` (batch).
   */
  const applyTaskMutationItems = useCallback(
    (batch: TaskMutationBatchItem[], isMultiBatch: boolean) => {
      if (batch.length === 0) return;

      const {
        appliedCount,
        updatedProjects,
        mutatedTasksForRawSync,
        recurringToSpawn,
        hasMilestoneCelebration,
        hasMajorChanges,
        singleTaskLogDescription,
      } = executeTaskMutationBatch({
        batch,
        allProjects: tracker.getAllProjects(),
        fallbackTasks: tasks,
        currentProjectId,
        COMM_PROJECT_ID,
        INBOX_PROJECT_ID,
        userEmail: user?.email || undefined,
        normalizeProjectId,
        markProjectDirty,
        onMarkTaskDirty,
      });

      if (appliedCount === 0) return;

      if (isMultiBatch || hasMajorChanges) {
        saveSnapshot();
      }

      if (hasMilestoneCelebration) {
        confetti({
          particleCount: 150,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#F59E0B', '#FCD34D', '#FFFBEB'],
          zIndex: 10000,
        });
      }

      tracker.commitProjects(updatedProjects, setAllProjects);
      tracker.syncMultipleTasks(mutatedTasksForRawSync, true);

      const currentInNew = updatedProjects.find(p => areProjectIdsMatching(p.id, currentProjectId));
      if (currentInNew) {
        updateCurrentProjectTasks(currentInNew, updatedProjects);
        setProjectMetadata(currentInNew.metadata || {});
      }

      if (isMultiBatch) {
        addLogEntry('update', `${batch.length} Aufgaben aktualisiert`);
      } else if (hasMajorChanges && singleTaskLogDescription) {
        addLogEntry('update', singleTaskLogDescription);
      }

      recurringToSpawn.forEach(({ orig, finalUpdates, projectId }) => {
        const activeRecurrence =
          finalUpdates.recurrence !== undefined ? finalUpdates.recurrence : orig.recurrence;
        if (activeRecurrence && activeRecurrence.frequency !== 'none') {
          const nextTaskData = generateNextRecurringTask({
            ...orig,
            ...finalUpdates,
            recurrence: activeRecurrence,
          });
          if (nextTaskData) {
            setTimeout(() => {
              if (addTaskRef.current) {
                addTaskRef.current(
                  nextTaskData.type || 'task',
                  orig.parentId,
                  projectId,
                  nextTaskData.name || orig.name,
                  nextTaskData,
                  { bypassDragGuard: true }
                );
              }
            }, 60);
          }
        }
      });
    },
    [
      currentProjectId,
      saveSnapshot,
      addLogEntry,
      INBOX_PROJECT_ID,
      COMM_PROJECT_ID,
      user?.email,
      tracker,
      tasks,
      normalizeProjectId,
      markProjectDirty,
      onMarkTaskDirty,
      setAllProjects,
      setProjectMetadata,
      updateCurrentProjectTasks,
    ]
  );

  const updateTask = useCallback(
    (id: number | string, updates: Partial<Task>, projectId?: string) => {
      applyTaskMutationItems([{ id, updates, projectId }], false);
    },
    [applyTaskMutationItems]
  );

  const updateTasksMultiple = useCallback(
    (taskUpdates: { id: string | number; updates: Partial<Task>; projectId?: string }[]) => {
      applyTaskMutationItems(taskUpdates, true);
    },
    [applyTaskMutationItems]
  );

  // Sub-domain 1: Project Metadata & Category Actions
  const { updateProjectMetadata, updateProjectCategory } = useProjectMetadataActions({
    allProjectsRef,
    setAllProjects,
    currentProjectId,
    setProjectMetadata,
    markProjectDirty,
    INBOX_PROJECT_ID,
    COMM_PROJECT_ID,
    tenantId,
    normalizeProjectId,
  });

  // Sub-domain 2: Task & Contact Deletion + Transactional Undo
  const { deleteTask } = useTaskDeleteActions({
    syncTracker: tracker,
    setAllProjects,
    currentProjectId,
    tasks,
    setTasks,
    setProjectMetadata,
    addLogEntry,
    markProjectDirty,
    onMarkTaskDirty,
    updateCurrentProjectTasks,
    COMM_PROJECT_ID,
  });

  // Sub-domain 3: Task Save/Move & Duplication
  const { handleSaveTask, duplicateTask } = useTaskDuplicateAndMove({
    syncTracker: tracker,
    setAllProjects,
    currentProjectId,
    tasks,
    setTasks,
    saveSnapshot,
    addLogEntry,
    markProjectDirty,
    onMarkTaskDirty,
    updateTask,
    updateCurrentProjectTasks,
    user,
    tenantId,
    COMM_PROJECT_ID,
    INBOX_PROJECT_ID,
    normalizeProjectId,
  });

  // Sub-domain 4: Task Creation
  const { addTask } = useTaskAddAction({
    currentProjectId,
    tracker,
    setAllProjects,
    setTasks,
    markProjectDirty,
    onMarkTaskDirty,
    addLogEntry,
    user,
    tenantId,
    COMM_PROJECT_ID,
    INBOX_PROJECT_ID,
    normalizeProjectId,
  });

  addTaskRef.current = addTask;

  return {
    commitTasks,
    updateTask,
    updateTasksMultiple,
    updateProjectMetadata,
    updateProjectCategory,
    deleteTask,
    handleSaveTask,
    addTask,
    duplicateTask,
  };
}
